import type { Appointment } from "@src/packages/care/hooks/useAppointments";
import type { TranslationFunction } from "@src/packages/i18n";
import type { Phase } from "../hooks/useCycles";

/**
 * How to get ready for the egg retrieval, in one set of words for every surface
 * that says it.
 *
 * Four places used to give four different fasting rules for the same morning:
 * "no solid food in the 8 h before", "nothing to eat or drink after midnight",
 * "often around 8 hours before". Under sedation, food and drink are not the same
 * advice, and only her clinic knows its own timing. So when her clinic has put
 * its instructions on record, those are what she reads, in full. When it has
 * not, every surface sends her to the clinic's timing without inventing one.
 */

type RetrievalPhase = Pick<Phase, "type" | "procedures">;
type RetrievalProcedure = NonNullable<Phase["procedures"]>[number];
type RetrievalAppointment = Pick<
  Appointment,
  "id" | "description" | "source" | "verificationStatus"
>;

export type RetrievalPreparationPerspective = "self" | "partner";

const NS = "care.retrieval_preparation";

/** Written by her clinic, and not since refuted. */
function isFromClinic(record: {
  source: string;
  verificationStatus: string;
}): boolean {
  return (
    record.source === "provider" && record.verificationStatus !== "refuted"
  );
}

function retrievalProcedures(
  phases: readonly RetrievalPhase[],
): RetrievalProcedure[] {
  return phases
    .flatMap((phase) => phase.procedures ?? [])
    .filter(
      (procedure) =>
        procedure.procedureType === "egg_retrieval" &&
        procedure.verificationStatus !== "refuted",
    );
}

/**
 * The appointments a retrieval procedure is booked through. A caller only needs
 * the appointment list when there is one to look up.
 */
export function retrievalAppointmentIds(
  phases: readonly RetrievalPhase[],
): string[] {
  return retrievalProcedures(phases)
    .map((procedure) => procedure.appointmentId)
    .filter((id): id is string => !!id);
}

/**
 * The retrieval instructions her clinic recorded, or null when it recorded none.
 *
 * Read from the egg-retrieval procedure on the cycle: the description of the
 * appointment it is booked through, which is written to her. It counts only
 * when her clinic wrote it, because the same field also holds what she typed
 * herself. The procedure's own notes are left out: a clinic may keep internal
 * clinical notes there that were never meant for her.
 */
export function findRetrievalClinicInstructions(params: {
  phases: readonly RetrievalPhase[];
  appointments: readonly RetrievalAppointment[];
}): string | null {
  const texts: string[] = [];
  const add = (text: string | null | undefined) => {
    const trimmed = text?.trim();
    if (trimmed && !texts.includes(trimmed)) texts.push(trimmed);
  };
  for (const procedure of retrievalProcedures(params.phases)) {
    const appointment = procedure.appointmentId
      ? params.appointments.find(
          (candidate) => candidate.id === procedure.appointmentId,
        )
      : undefined;
    if (appointment && isFromClinic(appointment)) add(appointment.description);
  }
  return texts.length > 0 ? texts.join("\n\n") : null;
}

export type RetrievalPreparation = {
  /**
   * When to stop eating and drinking: her clinic's own instructions, labelled as
   * theirs, or the one line that sends her to the clinic's timing.
   */
  fasting: string;
  /**
   * What holds whatever the clinic's timing: photo ID, comfortable clothes, a
   * ride home, and jewelry only when the clinic asks.
   */
  practical: string[];
};

export function presentRetrievalPreparation(params: {
  t: TranslationFunction;
  clinicInstructions: string | null;
  perspective?: RetrievalPreparationPerspective;
}): RetrievalPreparation {
  const { t, clinicInstructions } = params;
  if (params.perspective === "partner") {
    return {
      fasting: clinicInstructions
        ? t(`${NS}.partner.from_the_clinic`, {
            params: { instructions: clinicInstructions },
          })
        : t(
            `${NS}.partner.clinic_tells_your_partner_when_to_stop_eating_and_drinking`,
          ),
      practical: [
        t(`${NS}.partner.plan_to_take_your_partner_home`),
        t(`${NS}.partner.jewelry_and_nail_polish_only_if_the_clinic_asks`),
      ],
    };
  }
  return {
    fasting: clinicInstructions
      ? t(`${NS}.from_your_clinic`, {
          params: { instructions: clinicInstructions },
        })
      : t(`${NS}.clinic_tells_you_when_to_stop_eating_and_drinking`),
    practical: [
      t(`${NS}.bring_photo_id_and_wear_comfortable_clothes`),
      t(`${NS}.have_someone_take_you_home`),
      t(`${NS}.jewelry_and_nail_polish_only_if_your_clinic_asks`),
    ],
  };
}
